import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Menu, MenuItem, FormControlLabel, Checkbox, TablePagination,
  Tooltip, Stack, Button, CircularProgress, Typography, alpha
} from '@mui/material';
import { MoreVert, ViewColumn, Download } from '@mui/icons-material';

// Lightweight CSV export helper
function toCSV(rows, columns) {
  const visibleCols = columns.filter(c => c.visible !== false);
  const header = visibleCols.map(c => `"${(c.label||c.id).replace(/"/g, '""')}"`).join(',');
  const body = rows.map(r => visibleCols.map(c => {
    const v = typeof c.render === 'function' ? c.accessor ? c.accessor(r) : (r[c.id]) : (r[c.id]);
    const text = v === undefined || v === null ? '' : String(v);
    return `"${text.replace(/"/g, '""')}"`;
  }).join(',')).join('\n');
  return header + '\n' + body;
}

export default function EnterpriseTable({
  columns = [],
  fetchData, // async ({ page, limit, search, filters }) => ({ data, total, pages })
  initialPage = 1,
  initialLimit = 20,
  rowKey = '_id',
  enableExport = true,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(initialPage - 1);
  const [limit, setLimit] = useState(initialLimit);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [cols, setCols] = useState(columns.map(c => ({ ...c, visible: c.visible !== false })));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  async function load() {
    if (!fetchData) return;
    setLoading(true);
    try {
      const res = await fetchData({ page: page + 1, limit });
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('EnterpriseTable load error', e);
    } finally {
      setLoading(false);
    }
  }

  function toggleColumn(id) {
    setCols(prev => prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c));
  }

  function handleExport() {
    const csv = toCSV(data, cols);
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {enableExport && (
            <Tooltip title="Export current page as CSV">
              <Button startIcon={<Download />} size="small" variant="outlined" onClick={handleExport}>Export CSV</Button>
            </Tooltip>
          )}
        </Stack>
        <Box>
          <Tooltip title="Choose visible columns">
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ border: 1, borderColor: 'divider' }}>
              <ViewColumn />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { mt: 0.75, minWidth: 240 } }}
          >
            <MenuItem disabled>
              <Typography variant="caption" fontWeight={800}>Columns</Typography>
            </MenuItem>
            {cols.map(col => (
              <MenuItem key={col.id} dense>
                <FormControlLabel
                  control={<Checkbox size="small" checked={col.visible} onChange={() => toggleColumn(col.id)} />}
                  label={col.label || col.id}
                  sx={{ width: '100%', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          overflow: 'auto',
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
        }}
      >
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ pl: 2 }} />
              {cols.filter(c => c.visible).map(c => (
                <TableCell key={c.id} sx={{ whiteSpace: 'nowrap' }}>{c.label || c.id}</TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={cols.filter(c => c.visible).length + 2} sx={{ textAlign: 'center', py: 7 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading records...</Typography>
              </TableCell></TableRow>
            ) : data.length === 0 ? (
              <TableRow><TableCell colSpan={cols.filter(c => c.visible).length + 2} sx={{ textAlign: 'center', py: 7 }}>
                <Typography fontWeight={700}>No records found</Typography>
                <Typography variant="body2" color="text.secondary">Try adjusting filters or add a new record.</Typography>
              </TableCell></TableRow>
            ) : data.map(row => {
              const checked = !!selected.find(s => s === row[rowKey]);
              return (
                <TableRow key={row[rowKey]} hover selected={checked}>
                  <TableCell padding="checkbox" sx={{ pl: 2 }}>
                    <Checkbox size="small" checked={checked} onChange={(e) => setSelected(prev => e.target.checked ? [...prev, row[rowKey]] : prev.filter(x => x !== row[rowKey]))} />
                  </TableCell>
                  {cols.filter(c => c.visible).map(c => (
                    <TableCell key={c.id}>
                      {c.render ? c.render(row) : (c.accessor ? c.accessor(row) : row[c.id])}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha('#174EA6', 0.08), color: 'primary.main' } }}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10,20,50,100]}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderTop: 0,
          borderRadius: '0 0 8px 8px',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      />
    </Box>
  );
}
